import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getUsers, updateUser, deleteUser, changeUserPassword } from '../../actions/userActions';
import { clearUserUpdated, clearUserDeleted, clearPasswordChanged, clearError } from '../../slices/userSlice';
import Loader from '../layouts/Loader';
import Sidebar from '../Dashboard/Sidebar';
import { toast } from 'react-toastify';
import formatDate from '../../utils/formatDate';

export default function UserList() {
    const dispatch = useDispatch();
    const { users = [], loading = true, isUserUpdated, isUserDeleted, isPasswordChanged, error } = useSelector(state => state.userState);
    const { user: currentUser } = useSelector(state => state.authState);

    const isMaster = currentUser?.role === 'master';

    const [editModal, setEditModal] = useState({ show: false, user: null });
    const [selectedRole, setSelectedRole] = useState('');
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });
    const [passwordModal, setPasswordModal] = useState({ show: false, user: null, password: '', confirmPassword: '' });

    const roles = ['master', 'admin', 'manager', 'doctor', 'user'];

    useEffect(() => {
        dispatch(clearUserUpdated());
        dispatch(clearUserDeleted());
        dispatch(clearPasswordChanged());
        dispatch(clearError());
        dispatch(getUsers);
        return () => {
            dispatch(clearUserUpdated());
            dispatch(clearUserDeleted());
            dispatch(clearPasswordChanged());
            dispatch(clearError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isUserUpdated) {
            toast('User role updated successfully!', { type: 'success' });
            dispatch(clearUserUpdated());
            setEditModal({ show: false, user: null });
            dispatch(getUsers);
        }

        if (isUserDeleted) {
            toast('User deleted successfully!', { type: 'success' });
            dispatch(clearUserDeleted());
            setDeleteModal({ show: false, id: null, name: '' });
            dispatch(getUsers);
        }

        if (isPasswordChanged) {
            toast('Password updated successfully!', { type: 'success' });
            dispatch(clearPasswordChanged());
            setPasswordModal({ show: false, user: null, password: '', confirmPassword: '' });
            dispatch(getUsers);
        }

        if (error) {
            toast(error, { type: 'error' });
            dispatch(clearError());
        }
    }, [isUserUpdated, isUserDeleted, isPasswordChanged, error, dispatch]);

    const openPasswordModal = (user) => {
        setPasswordModal({ show: true, user, password: '', confirmPassword: '' });
    };

    const handleSavePassword = () => {
        if (!passwordModal.user) return;
        if (!isMaster) {
            toast('Only Master Admin can change passwords', { type: 'error' });
            return;
        }
        if (!passwordModal.password || passwordModal.password.length < 6) {
            toast('Password must be at least 6 characters long', { type: 'error' });
            return;
        }
        if (passwordModal.password !== passwordModal.confirmPassword) {
            toast('Passwords do not match', { type: 'error' });
            return;
        }
        dispatch(changeUserPassword(passwordModal.user._id || passwordModal.user.id, {
            password: passwordModal.password
        }));
    };

    const openEditModal = (user) => {
        setEditModal({ show: true, user });
        setSelectedRole(user.role);
    };

    const handleSaveRole = () => {
        if (!editModal.user) return;
        if (!isMaster) {
            toast('Only Master Admin can edit user roles', { type: 'error' });
            return;
        }
        dispatch(updateUser(editModal.user._id || editModal.user.id, { role: selectedRole }));
    };

    const handleDeleteUser = (id, name) => {
        setDeleteModal({ show: true, id, name });
    };

    const confirmDelete = () => {
        if (deleteModal.id) {
            dispatch(deleteUser(deleteModal.id));
        }
    };

    const getRoleBadge = (role) => {
        switch (role) {
            case 'master':
                return <span className="badge" style={{ background: '#7c3aed', color: '#fff', fontSize: '0.8rem', padding: '0.4em 0.7em' }}>Master Admin</span>;
            case 'admin':
                return <span className="badge bg-primary" style={{ fontSize: '0.8rem', padding: '0.4em 0.7em' }}>Admin</span>;
            case 'manager':
                return <span className="badge bg-info text-dark" style={{ fontSize: '0.8rem', padding: '0.4em 0.7em' }}>Manager</span>;
            case 'doctor':
                return <span className="badge bg-success" style={{ fontSize: '0.8rem', padding: '0.4em 0.7em' }}>Doctor</span>;
            default:
                return <span className="badge bg-secondary" style={{ fontSize: '0.8rem', padding: '0.4em 0.7em' }}>User</span>;
        }
    };

    return (
        <div className="row g-0">
            <div className="col-12 col-md-2"><Sidebar /></div>
            <div className="col-12 col-md-10 main-content">
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <div>
                        <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>User Management</h1>
                        <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            {users.length} registered user{users.length !== 1 ? 's' : ''} in the system
                        </p>
                    </div>
                    {isMaster && (
                        <Link
                            to="/admin/users/new"
                            className="btn btn-primary"
                        >
                            <i className="fa fa-user-plus me-2"></i>Add User
                        </Link>
                    )}
                </div>

                <div className="card shadow-sm">
                    <div className="card-body p-3">
                        {loading ? (
                            <Loader />
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Role</th>
                                            <th>Joined Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((u, index) => (
                                            <tr key={u._id || u.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="fw-semibold">{u.name}</div>
                                                    {u._id === currentUser?._id && (
                                                        <small className="text-muted">(You)</small>
                                                    )}
                                                </td>
                                                <td>{u.email}</td>
                                                <td>{getRoleBadge(u.role)}</td>
                                                <td>{formatDate(u.createdAt)}</td>
                                                <td>
                                                    {isMaster ? (
                                                        <Fragment>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => openEditModal(u)}
                                                                disabled={u.role === 'master' && u._id !== currentUser?._id}
                                                                title="Change Role"
                                                            >
                                                                <i className="fa fa-user-shield me-1"></i> Role
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-warning me-2 text-dark"
                                                                onClick={() => openPasswordModal(u)}
                                                                title="Change Password"
                                                            >
                                                                <i className="fa fa-key me-1"></i> Password
                                                            </button>
                                                            {u.role !== 'master' && u._id !== currentUser?._id && (
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDeleteUser(u._id || u.id, u.name)}
                                                                    title="Delete User"
                                                                >
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            )}
                                                        </Fragment>
                                                    ) : (
                                                        <span className="badge bg-light text-muted border">Read Only</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* EDIT ROLE MODAL */}
                {editModal.show && (
                    <div className="modal d-block tab-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">
                                        Change Role for {editModal.user?.name}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setEditModal({ show: false, user: null })}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p className="text-muted small mb-3">
                                        Select the new access role for <strong>{editModal.user?.email}</strong>:
                                    </p>

                                    <div className="form-group mb-3">
                                        <label className="fw-bold mb-2">Select Role</label>
                                        <select
                                            className="form-select"
                                            value={selectedRole}
                                            onChange={(e) => setSelectedRole(e.target.value)}
                                        >
                                            {roles.map((r) => (
                                                <option key={r} value={r}>
                                                    {r === 'master' ? 'Master Admin (Full Access)' : r.toUpperCase()}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {selectedRole === 'master' && (
                                        <div className="alert alert-warning py-2 small mb-0">
                                            <i className="fa fa-exclamation-triangle me-1"></i>
                                            Master Admin has full administrative control including role management.
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setEditModal({ show: false, user: null })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleSaveRole}
                                    >
                                        Save Role
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* DELETE CONFIRM MODAL */}
                {deleteModal.show && (
                    <div className="modal d-block tab-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow">
                                <div className="modal-header border-bottom-0">
                                    <h5 className="modal-title fw-bold text-danger">Confirm Delete User</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setDeleteModal({ show: false, id: null, name: '' })}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    Are you sure you want to delete user <strong>{deleteModal.name}</strong>? This action cannot be undone.
                                </div>
                                <div className="modal-footer border-top-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setDeleteModal({ show: false, id: null, name: '' })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={confirmDelete}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* CHANGE PASSWORD MODAL */}
                {passwordModal.show && (
                    <div className="modal d-block tab-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content shadow border-0">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">
                                        <i className="fa fa-key me-2 text-warning"></i>
                                        Change Password for {passwordModal.user?.name}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setPasswordModal({ show: false, user: null, password: '', confirmPassword: '' })}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p className="text-muted small mb-3">
                                        Set a new password for <strong>{passwordModal.user?.email}</strong>.
                                        {passwordModal.user?._id === currentUser?._id && (
                                            <span className="text-primary ms-1 fw-semibold">(Your own account)</span>
                                        )}
                                    </p>
                                    <div className="form-group mb-3">
                                        <label className="fw-semibold mb-1">New Password (Min. 6 chars)</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            placeholder="Enter new password"
                                            value={passwordModal.password}
                                            onChange={(e) => setPasswordModal({ ...passwordModal, password: e.target.value })}
                                        />
                                    </div>
                                    <div className="form-group mb-3">
                                        <label className="fw-semibold mb-1">Confirm New Password</label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            placeholder="Confirm new password"
                                            value={passwordModal.confirmPassword}
                                            onChange={(e) => setPasswordModal({ ...passwordModal, confirmPassword: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setPasswordModal({ show: false, user: null, password: '', confirmPassword: '' })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-warning text-dark fw-semibold"
                                        onClick={handleSavePassword}
                                    >
                                        <i className="fa fa-check me-1"></i> Update Password
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

